import React, { useState } from 'react';
import { Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent } from '../ui/card';
import { toast } from 'sonner';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    plantType: '',
    capacity: '',
    location: '',
    message: '',
    inquiryType: 'quote'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/submit-contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to submit contact form');
      }
      
      const result = await response.json();
      toast.success(result.message);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        plantType: '',
        capacity: '',
        location: '',
        message: '',
        inquiryType: 'quote'
      });
    } catch (error) {
      toast.error(error.message || 'Unable to submit request. Please call us at +91-9351292512');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="lg:col-span-2">
      <Card className="border-2 border-gray-200 bg-white">
        <CardContent className="p-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Get a <span className="text-emerald-600">Free Quote</span>
          </h2>
          <p className="text-gray-600 mb-8">
            Fill out the form below and our team will get back to you within 24 hours with a detailed quotation.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <Input
                  type="text"
                  name="name"
                  data-testid="contact-name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Enter your name"
                  className="w-full"
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <Input
                  type="email"
                  name="email"
                  data-testid="contact-email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="your@email.com"
                  className="w-full"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <Input
                  type="tel"
                  name="phone"
                  data-testid="contact-phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  placeholder="+91-XXXXXXXXXX"
                  className="w-full"
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name
                </label>
                <Input
                  type="text"
                  name="company"
                  data-testid="contact-company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Your company name"
                  className="w-full"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Plant Type *
                </label>
                <select
                  name="plantType"
                  data-testid="contact-plant-type"
                  value={formData.plantType}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                >
                  <option value="">Select plant type</option>
                  <option value="mineral-water">Mineral Water Plant</option>
                  <option value="soft-drink">Soft Drink Plant</option>
                  <option value="juice">Juice Processing Plant</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Desired Capacity
                </label>
                <Input
                  type="text"
                  name="capacity"
                  data-testid="contact-capacity"
                  value={formData.capacity}
                  onChange={handleChange}
                  placeholder="e.g., 6000 BPH"
                  className="w-full"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location *
              </label>
              <Input
                type="text"
                name="location"
                data-testid="contact-location"
                value={formData.location}
                onChange={handleChange}
                required
                placeholder="City, State"
                className="w-full"
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message / Requirements
              </label>
              <Textarea
                name="message"
                data-testid="contact-message"
                value={formData.message}
                onChange={handleChange}
                rows={5}
                placeholder="Tell us about your project requirements..."
                className="w-full"
                disabled={isSubmitting}
              />
            </div>

            <Button 
              type="submit" 
              size="lg"
              data-testid="contact-submit"
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              {!isSubmitting && <Send className="mr-2" />}
              {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ContactForm;
